import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { fetchCategories, fetchProduct, createProduct, updateProduct } from "@/lib/api";
import { SIZE_OPTIONS, COLOR_OPTIONS, FEATURE_OPTIONS } from "@/lib/productOptions";
import ImageUploader from "@/components/admin/ImageUploader";

const EMPTY = {
  name: "", sku: "", category: "", fabric: "", price: "", description: "",
  sizes: [], colors: [], features: [], images: [], featured: false, active: true,
};

const inputCls = "w-full rounded-lg border border-zinc-300 px-3.5 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";
const labelCls = "mb-1.5 block text-sm font-medium text-zinc-700";

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${
        active ? "border-primary bg-primary text-white" : "border-zinc-300 text-zinc-600 hover:border-primary/50"
      }`}>
      {active && <Check className="h-3.5 w-3.5" />} {children}
    </button>
  );
}

export default function ProductForm() {
  const { id } = useParams();
  const isEdit = id && id !== "new";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeColorName, setActiveColorName] = useState(null);
  
  // Çift renk kombinasyonu için state'ler
  const [combo1, setCombo1] = useState("");
  const [combo2, setCombo2] = useState("");

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });
  const { data: product } = useQuery({
    queryKey: ["admin-product", id], queryFn: () => fetchProduct(id), enabled: !!isEdit,
  });

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || "", sku: product.sku || "", category: product.category || "",
        fabric: product.fabric || "", price: product.price ?? "", description: product.description || "",
        sizes: product.sizes || [], colors: product.colors || [], features: product.features || [],
        images: product.images || [], featured: !!product.featured, active: product.active !== false,
      });
    }
  }, [product]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, val, key = null) => {
    const arr = form[k];
    const exists = key ? arr.some((x) => x[key] === val[key]) : arr.includes(val);
    set(k, exists ? arr.filter((x) => (key ? x[key] !== val[key] : x !== val)) : [...arr, val]);
  };

  // İki farklı rengi birleştiren fonksiyon
  const handleAddCombo = () => {
    if (!combo1 || !combo2) {
      toast.error("Lütfen birleştirmek için iki renk seçin.");
      return;
    }
    if (combo1 === combo2) {
      toast.error("Farklı iki renk seçmelisiniz.");
      return;
    }

    const c1 = COLOR_OPTIONS.find((c) => c.name === combo1);
    const c2 = COLOR_OPTIONS.find((c) => c.name === combo2);

    if (c1 && c2) {
      const comboName = `${c1.name} & ${c2.name}`;
      const comboHex = `linear-gradient(135deg, ${c1.hex} 50%, ${c2.hex} 50%)`;

      if (form.colors.some((c) => c.name === comboName)) {
        toast.error("Bu renk kombinasyonu zaten eklendi.");
        return;
      }

      set("colors", [...form.colors, { name: comboName, hex: comboHex, images: [] }]);
      setCombo1("");
      setCombo2("");
    }
  };

  const activeColor = form.colors.find((c) => c.name === activeColorName) || form.colors[0] || null;
  const updateColorImages = (colorName, imgs) => {
    set("colors", form.colors.map((c) => (c.name === colorName ? { ...c, images: imgs } : c)));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Ürün adı zorunlu";
    if (!form.category) e.category = "Kategori seçin";
    if (form.price === "" || isNaN(Number(form.price)) || Number(form.price) < 0) e.price = "Geçerli bir fiyat girin";
    if (form.images.length === 0) e.images = "En az 1 görsel ekleyin";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Lütfen eksik alanları doldurun"); return; }
    setSaving(true);
    const payload = { ...form, price: Number(form.price) };
    try {
      if (isEdit) await updateProduct(id, payload);
      else await createProduct(payload);
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      toast.success(isEdit ? "Ürün güncellendi" : "Ürün eklendi");
      navigate("/admin");
    } catch (err) {
      toast.error("Kaydetme başarısız: " + (err.response?.data?.detail || ""));
    } finally {
      setSaving(false);
    }
  };

  const customCombos = form.colors.filter((fc) => !COLOR_OPTIONS.some((opt) => opt.name === fc.name));

  return (
    <form onSubmit={submit} data-testid="product-form" className="mx-auto max-w-3xl pb-24">
      <button type="button" onClick={() => navigate("/admin")} className="mb-4 flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900">
        <ArrowLeft className="h-4 w-4" /> Ürünlere dön
      </button>
      <h1 className="mb-6 text-2xl font-bold">{isEdit ? "Ürünü Düzenle" : "Yeni Ürün Ekle"}</h1>

      <div className="space-y-6">
        {/* Basics */}
        <div className="rounded-xl border border-zinc-200 bg-white p-5 md:p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-zinc-400">Temel Bilgiler</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelCls}>Ürün Adı *</label>
              <input data-testid="pf-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Örn: Softshell Su Geçirmez Mont" />
              {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
            </div>
            <div>
              <label className={labelCls}>Ürün Kodu (SKU)</label>
              <input data-testid="pf-sku" className={inputCls} value={form.sku} onChange={(e) => set("sku", e.target.value)} placeholder="Örn: MN-002" />
            </div>
            <div>
              <label className={labelCls}>Kategori *</label>
              <select data-testid="pf-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
                <option value="">Seçiniz</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              {errors.category && <p className="mt-1 text-xs text-red-600">{errors.category}</p>}
            </div>
            <div>
              <label className={labelCls}>Fiyat (₺) *</label>
              <input data-testid="pf-price" type="number" min="0" step="1" className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="0" />
              {errors.price && <p className="mt-1 text-xs text-red-600">{errors.price}</p>}
            </div>
            <div>
              <label className={labelCls}>Kumaş Türü</label>
              <input data-testid="pf-fabric" className={inputCls} value={form.fabric} onChange={(e) => set("fabric", e.target.value)} placeholder="Örn: Softshell" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Açıklama</label>
              <textarea data-testid="pf-description" rows={3} className={`${inputCls} resize-none`} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Ürün hakkında kısa açıklama..." />
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="rounded-xl border border-zinc-200 bg-white p-5 md:p-6">
          <h2 className="mb-1 text-sm font-semibold uppercase tracking-wider text-zinc-400">Genel / Kapak Görselleri *</h2>
          <p className="mb-4 text-xs text-zinc-400">İlk görsel kapak fotoğrafı olur. Rengi olmayan ürünlerde ve kendi görseli olmayan renklerde bu galeri gösterilir. Sürükleyerek sıralayabilirsiniz.</p>
          
          {/* REVİZE: Genel görseller için max limit 20 yapıldı */}
          <ImageUploader value={form.images} onChange={(imgs) => set("images", imgs)} max={20} />
          
          {errors.images && <p className="mt-2 text-xs text-red-600">{errors.images}</p>}
        </div>

        {/* Sizes */}
        <div className="rounded-xl border border-zinc-200 bg-white p-5 md:p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-zinc-400">Beden Seçenekleri</h2>
          <div className="flex flex-wrap gap-2">
            {SIZE_OPTIONS.map((s) => (
              <Chip key={s} testid={`size-${s}`} active={form.sizes.includes(s)} onClick={() => toggle("sizes", s)}>{s}</Chip>
            ))}
          </div>
        </div>

        {/* Colors */}
        <div className="rounded-xl border border-zinc-200 bg-white p-5 md:p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-zinc-400">Renk Seçenekleri</h2>
          
          <div className="flex flex-wrap gap-2">
            {COLOR_OPTIONS.map((c) => {
              const active = form.colors.some((x) => x.name === c.name);
              return (
                <Chip key={c.name} testid={`color-${c.name}`} active={active} onClick={() => toggle("colors", { ...c, images: [] }, "name")}>
                  <span className="h-3.5 w-3.5 rounded-full border border-zinc-300" style={{ background: c.hex }} /> {c.name}
                </Chip>
              );
            })}
          </div>

          <div className="mt-6 rounded-lg bg-zinc-50 p-4 border border-zinc-100">
            <p className="mb-3 text-xs font-semibold uppercase text-zinc-500">Çift Renkli Kombinasyon Oluştur</p>
            <div className="flex flex-wrap items-center gap-3">
              <select className={`${inputCls} max-w-[140px] py-2`} value={combo1} onChange={(e) => setCombo1(e.target.value)}>
                <option value="">1. Renk</option>
                {COLOR_OPTIONS.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
              <span className="text-zinc-400">+</span>
              <select className={`${inputCls} max-w-[140px] py-2`} value={combo2} onChange={(e) => setCombo2(e.target.value)}>
                <option value="">2. Renk</option>
                {COLOR_OPTIONS.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
              <button type="button" onClick={handleAddCombo} className="rounded-lg bg-zinc-800 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-zinc-700">
                Kombinle ve Ekle
              </button>
            </div>

            {customCombos.length > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-200">
                <p className="mb-2 text-xs text-zinc-400">Oluşturulan Kombinasyonlar (Kaldırmak için tıklayın)</p>
                <div className="flex flex-wrap gap-2">
                  {customCombos.map((combo) => (
                    <Chip key={combo.name} active={true} onClick={() => toggle("colors", combo, "name")} testid={`combo-${combo.name}`}>
                      <span className="h-3.5 w-3.5 rounded-full border border-zinc-300" style={{ background: combo.hex }} /> {combo.name}
                    </Chip>
                  ))}
                </div>
              </div>
            )}
          </div>

          {form.colors.length > 0 && (
            <div className="mt-5 border-t border-zinc-100 pt-5">
              <p className="mb-3 text-xs text-zinc-400">Bir renk/kombinasyon seçip sadece ona ait görselleri yükleyin. Kendi görseli olmayan renklerde yukarıdaki genel galeri kullanılır.</p>
              <div className="mb-4 flex flex-wrap gap-2">
                {form.colors.map((c) => {
                  const count = c.images?.length || 0;
                  const isActive = activeColor?.name === c.name;
                  return (
                    <button
                      type="button"
                      key={c.name}
                      data-testid={`color-tab-${c.name}`}
                      onClick={() => setActiveColorName(c.name)}
                      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                        isActive ? "border-primary bg-primary text-white" : "border-zinc-300 text-zinc-600 hover:border-primary/50"
                      }`}
                    >
                      <span className="h-3.5 w-3.5 rounded-full border border-zinc-300" style={{ background: c.hex }} />
                      {c.name}
                      
                      {/* REVİZE: Sekme sayaçlarındaki 15 değeri 20 yapıldı */}
                      <span className={`text-xs ${isActive ? "text-white/80" : "text-zinc-400"}`}>{count}/20</span>
                      
                    </button>
                  );
                })}
              </div>

              {activeColor && (
                <div>
                  <p className="mb-2 text-sm font-medium text-zinc-700">
                    {activeColor.name} görselleri 
                    
                    {/* REVİZE: Alt sekme alanındaki 15 değeri 20 yapıldı */}
                    <span className="text-zinc-400"> ({activeColor.images?.length || 0}/20)</span>
                    
                  </p>
                  
                  {/* REVİZE: Renge özel görseller için max limit 20 yapıldı */}
                  <ImageUploader
                    key={activeColor.name}
                    value={activeColor.images || []}
                    onChange={(imgs) => updateColorImages(activeColor.name, imgs)}
                    max={20}
                  />
                  
                </div>
              )}
            </div>
          )}
        </div>

        {/* Features */}
        <div className="rounded-xl border border-zinc-200 bg-white p-5 md:p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-zinc-400">Teknik Özellikler</h2>
          <div className="flex flex-wrap gap-2">
            {FEATURE_OPTIONS.map((f) => (
              <Chip key={f} testid={`feature-${f}`} active={form.features.includes(f)} onClick={() => toggle("features", f)}>{f}</Chip>
            ))}
          </div>
        </div>

        {/* Status */}
        <div className="rounded-xl border border-zinc-200 bg-white p-5 md:p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-zinc-400">Durum</h2>
          <div className="flex flex-col gap-4 sm:flex-row sm:gap-8">
            <label className="flex cursor-pointer items-center gap-3">
              <input data-testid="pf-active" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-primary" />
              <span className="text-sm"><b>Yayında</b> — sitede görünür</span>
            </label>
            <label className="flex cursor-pointer items-center gap-3">
              <input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-primary" />
              <span className="text-sm"><b>Öne Çıkar</b> — katalogda vurgulanır</span>
            </label>
          </div>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-zinc-200 bg-white/95 px-5 py-3 backdrop-blur lg:ms-64">
        <div className="mx-auto flex max-w-3xl items-center justify-end gap-3">
          <button type="button" onClick={() => navigate("/admin")} className="rounded-lg border border-zinc-300 px-4 py-2.5 text-sm font-medium hover:bg-zinc-50">İptal</button>
          <button data-testid="pf-submit" type="submit" disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isEdit ? "Değişiklikleri Kaydet" : "Ürünü Kaydet"}
          </button>
        </div>
      </div>
    </form>
  );
}